import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BackToBackLcService } from '../../services/backToBackLc.service';
import { PERMISSIONS } from '../../constants/permissions';
import { B2bUtilization, BackToBackLc, CreateB2bUtilizationRequest } from '../../interfaces/commercial.interface';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate, formatDate } from '../../utils/dateUtils';

const today = () => toLocalIsoDate();
const EMPTY = { utilizationDate: today(), amount: '', reference: '', remarks: '' };

interface Props {
    open: boolean;
    onClose: () => void;
    masterLcId: string;
    b2b: BackToBackLc;
    onSaved?: () => void;
}

const B2bUtilizationDialog: React.FC<Props> = ({ open, onClose, masterLcId, b2b: b2bProp, onSaved }) => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.LC_MANAGE);

    // Headroom and status change every time a utilization is saved, so the dialog keeps its own
    // up-to-date copy of the back-to-back LC instead of showing the row it was opened with.
    const [b2b, setB2b] = useState<BackToBackLc>(b2bProp);
    const [rows, setRows] = useState<B2bUtilization[]>([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ ...EMPTY });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await BackToBackLcService.listUtilizations(institutionId, masterLcId, b2b.id, 0, 100);
            setRows(res.content);
        } catch (e) {
            toast.showApiError(e, 'Failed to load utilizations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { setB2b(b2bProp); }, [b2bProp]);
    useEffect(() => { if (open) load(); }, [open, b2b.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(f => ({ ...f, [k]: e.target.value }));

    const saveWhy = saving ? null
        : !form.amount ? 'Enter the amount.'
        : null;

    const handleCreate = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !form.amount) return;
        const dto: CreateB2bUtilizationRequest = {
            utilizationDate: form.utilizationDate,
            amount: Number(form.amount),
            reference: form.reference.trim() || undefined,
            remarks: form.remarks.trim() || undefined,
        };
        try {
            setSaving(true);
            await BackToBackLcService.createUtilization(institutionId, masterLcId, b2b.id, dto);
            toast.showSuccess('Utilization recorded');
            setForm({ ...EMPTY });
            load();
            try {
                setB2b(await BackToBackLcService.getById(institutionId, masterLcId, b2b.id));
            } catch (refreshErr) {
                toast.showApiError(refreshErr, 'Saved, but could not refresh the remaining headroom');
            }
            onSaved?.();
        } catch (e) {
            toast.showApiError(e, 'Failed to record utilization');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'commercial.b2bUtil.dialog.page' } as any}>
            <DialogTitle>Utilizations — {b2b.b2bLcNumber}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Remaining headroom: {formatMoney(b2b.remainingHeadroom, b2b.currencyCode)}
                </Typography>
                {loading ? <CircularProgress /> : (
                    <TableContainer data-help-section="commercial.b2bUtil.table" sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercial.b2bUtil.col.date">Date</TableCell>
                                    <TableCell align="right" data-help="commercial.b2bUtil.col.amount">Amount</TableCell>
                                    <TableCell data-help="commercial.b2bUtil.col.reference">Reference</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(u => (
                                    <TableRow key={u.id}>
                                        <TableCell>{formatDate(u.utilizationDate)}</TableCell>
                                        <TableCell align="right">{formatMoney(u.amount, b2b.currencyCode)}</TableCell>
                                        <TableCell>{u.reference ?? '—'}</TableCell>
                                    </TableRow>
                                ))}
                                {rows.length === 0 && (
                                    <TableRow><TableCell colSpan={3} align="center">No utilizations yet.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {canManage && b2b.status !== 'CLOSED' && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Record Utilization</Typography>
                        <Grid container spacing={2}>
                            <Grid size={6}>
                                <TextField data-help="commercial.b2bUtil.date" label="Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                                    value={form.utilizationDate} onChange={set('utilizationDate')} />
                            </Grid>
                            <Grid size={6}>
                                <TextField data-help="commercial.b2bUtil.amount" label="Amount" type="number" fullWidth value={form.amount} onChange={set('amount')} />
                            </Grid>
                            <Grid size={6}>
                                <TextField data-help="commercial.b2bUtil.reference" label="Reference" fullWidth value={form.reference} onChange={set('reference')} />
                            </Grid>
                            <Grid size={6}>
                                <TextField data-help="commercial.b2bUtil.remarks" label="Remarks" fullWidth value={form.remarks} onChange={set('remarks')} />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button data-help="commercial.b2bUtil.close" onClick={onClose}>Close</Button>
                {canManage && b2b.status !== 'CLOSED' && (
                    <Button data-help="commercial.b2bUtil.record" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !form.amount} onClick={handleCreate}>
                        {saving ? 'Saving…' : 'Record'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default B2bUtilizationDialog;
